/*
 * Ürün kartında iki adımlı hızlı sepete ekleme (snippets/product-card-quick-add.liquid)
 *
 * 1) "Sepete ekle" -> panel açılır
 * 2) Beden (+ pantolonda Boy) seçilir -> siyah "Sepete ekle" ürünü sepete ekler
 *
 * "Boy" ayrı bir Shopify seçeneği değil: pantolonlarda beden değeri "30/32"
 * (bel/boy) formatında. Ürün sayfasındaki pants-size-select ile aynı mantık.
 *
 * Sepete ekleme paneldeki <product-form> ile yapılıyor (product-form.js).
 * Seçim eksikse gönder tıklaması burada durduruluyor; böylece form gönderilmiyor
 * ve theme.liquid'deki adtc_button dinleyicisi boşuna add_to_cart olayı atmıyor.
 */
if (!customElements.get('product-card-quick-add')) {
  // Sayfada aynı anda tek panel açık kalıyor
  let openQuickAdd = null;
  // Masaüstü modunun (product-card-quick-add.css) tersi: ikonla açılan mobil/tablet modu
  const touchLayout = window.matchMedia('(max-width: 1023px), (hover: none)');

  document.addEventListener('click', (evt) => {
    if (openQuickAdd && !openQuickAdd.contains(evt.target)) openQuickAdd.close();
  });
  document.addEventListener('keydown', (evt) => {
    if (evt.key === 'Escape' && openQuickAdd) openQuickAdd.close({ restoreFocus: true });
  });

  customElements.define(
    'product-card-quick-add',
    class ProductCardQuickAdd extends HTMLElement {
      constructor() {
        super();

        this.trigger = this.querySelector('[data-quick-add-trigger]');
        this.icon = this.querySelector('[data-quick-add-icon]');
        this.closeButton = this.querySelector('[data-quick-add-close]');
        this.sizeButtons = Array.from(this.querySelectorAll('[data-quick-add-sizes] [data-value]'));
        this.lengthButtons = Array.from(this.querySelectorAll('[data-quick-add-lengths] [data-value]'));
        this.errorElement = this.querySelector('[data-quick-add-error]');
        this.submitButton = this.querySelector('[data-quick-add-submit]');
        this.idInput = this.querySelector('form input[name="id"]');

        this.isPants = this.hasAttribute('data-pants');
        this.sizeIndex = parseInt(this.dataset.sizeIndex, 10) || 0;
        this.selectedSize = '';
        this.selectedLength = '';
        this.onCardLeave = () => this.close();
        // Mobil: panel açıkken kartın başka bir yerine dokunmak ürün sayfasına
        // gitmesin, yalnızca paneli kapatsın.
        this.onCardClick = (evt) => {
          if (!this.classList.contains('is-open') || this.contains(evt.target) || !touchLayout.matches) return;
          evt.preventDefault();
          evt.stopPropagation();
          this.close();
        };

        this.trigger.addEventListener('click', (evt) => {
          this.open();
          // Klavyeyle açıldıysa odak panele geçsin (tetikleyici açıkken gizleniyor)
          if (evt.detail === 0) this.sizeButtons.find((button) => !button.disabled)?.focus();
        });
        this.icon?.addEventListener('click', (evt) => {
          evt.preventDefault();
          this.open();
        });
        this.closeButton?.addEventListener('click', (evt) => {
          evt.preventDefault();
          this.close({ restoreFocus: true });
        });
        this.sizeButtons.forEach((button) => button.addEventListener('click', () => this.selectSize(button)));
        this.lengthButtons.forEach((button) => button.addEventListener('click', () => this.selectLength(button)));
        this.submitButton.addEventListener('click', this.onSubmitClick.bind(this));
      }

      connectedCallback() {
        // Karttan çıkınca panel kapanıyor
        this.card = this.closest('.card-product') || this.parentElement;
        this.card.addEventListener('mouseleave', this.onCardLeave);
        this.card.addEventListener('click', this.onCardClick, true);
      }

      disconnectedCallback() {
        this.card?.removeEventListener('mouseleave', this.onCardLeave);
        this.card?.removeEventListener('click', this.onCardClick, true);
        this.stopWatchingCart();
        if (openQuickAdd === this) openQuickAdd = null;
      }

      open() {
        if (openQuickAdd && openQuickAdd !== this) openQuickAdd.close();
        openQuickAdd = this;

        this.classList.add('is-open');
        this.trigger.setAttribute('aria-expanded', 'true');
        this.icon?.setAttribute('aria-expanded', 'true');
        this.refreshAvailability();
      }

      close({ restoreFocus = false } = {}) {
        if (openQuickAdd === this) openQuickAdd = null;
        if (!this.classList.contains('is-open')) return;

        const hadFocus = this.contains(document.activeElement);
        this.classList.remove('is-open');
        this.trigger.setAttribute('aria-expanded', 'false');
        this.icon?.setAttribute('aria-expanded', 'false');
        this.setError('');
        if (restoreFocus && hadFocus) (touchLayout.matches ? this.icon : this.trigger)?.focus();
      }

      selectSize(button) {
        if (button.disabled) return;
        this.selectedSize = button.dataset.value;
        this.markSelected(this.sizeButtons, button);
        this.setError('');

        if (!this.isPants) return;

        // Bel değişince o bele ait boylar yeniden hesaplanıyor; seçili boy artık
        // stokta değilse seçim temizleniyor.
        this.refreshLengths();
        const lengthStillValid = this.lengthButtons.some(
          (b) => b.dataset.value === this.selectedLength && !b.disabled
        );
        if (!lengthStillValid) {
          this.selectedLength = '';
          this.markSelected(this.lengthButtons, null);
        }
      }

      selectLength(button) {
        if (button.disabled) return;
        this.selectedLength = button.dataset.value;
        this.markSelected(this.lengthButtons, button);
        this.setError('');
      }

      markSelected(buttons, selected) {
        buttons.forEach((b) => {
          b.classList.toggle('is-selected', b === selected);
          b.setAttribute('aria-pressed', b === selected ? 'true' : 'false');
        });
      }

      normalize(value) {
        return String(value ?? '').trim().toLowerCase();
      }

      // Beden dışındaki seçenekleri (Renk, Kalıp) kartın seçili varyantıyla aynı
      // olan varyantlar. Panel yalnızca bunlar arasında beden/boy seçtiriyor.
      get candidateVariants() {
        if (!this._candidateVariants) {
          let variants = [];
          let selectedOptions = [];
          try {
            variants = JSON.parse(this.querySelector('[data-quick-add-variants]').textContent);
            selectedOptions = JSON.parse(this.dataset.selectedOptions || '[]');
          } catch (e) {
            console.error('product-card-quick-add: varyant verisi okunamadı', e);
          }

          this._candidateVariants = variants.filter((variant) =>
            variant.options.every(
              (value, index) =>
                index === this.sizeIndex || this.normalize(value) === this.normalize(selectedOptions[index])
            )
          );
        }
        return this._candidateVariants;
      }

      // Boş bırakılan beden/boy "herhangi biri" anlamına geliyor
      sizeMatches(variant, size, length) {
        const value = variant.options[this.sizeIndex];
        if (!this.isPants) return this.normalize(value) === this.normalize(size);

        const [waist, variantLength] = String(value ?? '').split('/');
        return (
          (!size || this.normalize(waist) === this.normalize(size)) &&
          (!length || this.normalize(variantLength) === this.normalize(length))
        );
      }

      findAvailableVariant(size, length) {
        return this.candidateVariants.find((variant) => variant.available && this.sizeMatches(variant, size, length));
      }

      setDisabled(button, disabled) {
        button.disabled = disabled;
        button.classList.toggle('is-disabled', disabled);
      }

      refreshAvailability() {
        // Pantolonda bel, o bele ait herhangi bir boy stoktaysa seçilebilir
        this.sizeButtons.forEach((button) =>
          this.setDisabled(button, !this.findAvailableVariant(button.dataset.value, ''))
        );
        if (this.isPants) this.refreshLengths();
      }

      refreshLengths() {
        // Bel seçilmediyse boy, herhangi bir belde stoktaysa seçilebilir
        this.lengthButtons.forEach((button) =>
          this.setDisabled(button, !this.findAvailableVariant(this.selectedSize, button.dataset.value))
        );
      }

      setError(message) {
        this.errorElement.textContent = message || '';
        this.errorElement.hidden = !message;
      }

      onSubmitClick(evt) {
        const strings = window.variantStrings || {};
        let message = '';
        let variant = null;

        if (!this.selectedSize) {
          message = this.isPants ? strings.selectSizeLength : strings.selectSize;
        } else if (this.isPants && !this.selectedLength) {
          message = strings.selectSizeLength;
        } else {
          variant = this.findAvailableVariant(this.selectedSize, this.selectedLength);
          if (!variant) message = strings.unavailable;
        }

        if (!variant || this.submitButton.classList.contains('loading')) {
          // Form gönderilmesin, adtc_button dataLayer dinleyicisine de ulaşmasın
          evt.preventDefault();
          evt.stopPropagation();
          if (!variant) this.setError(message);
          return;
        }

        this.setError('');
        this.idInput.value = variant.id;
        this.setLoading(true);
        this.watchCart(String(variant.id));
      }

      setLoading(loading) {
        this.submitButton.classList.toggle('loading', loading);
        if (loading) {
          this.submitButton.setAttribute('aria-disabled', 'true');
        } else {
          this.submitButton.removeAttribute('aria-disabled');
        }
      }

      // product-form.js sonucu PUB_SUB_EVENTS ile yayınlıyor; başarılıysa panel
      // kapanıyor (sepet çekmecesi açılıyor), hata varsa panelde gösteriliyor.
      watchCart(variantId) {
        this.stopWatchingCart();

        const finish = (data, isError) => {
          if (String(data?.productVariantId) !== variantId) return;
          this.stopWatchingCart();
          this.setLoading(false);
          if (!isError) {
            this.close();
            return;
          }
          const errors = typeof data.errors === 'string' ? data.errors : '';
          this.setError(errors || data.message || window.cartStrings?.error || '');
        };

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.cartUnsubscribers = [
            subscribe(PUB_SUB_EVENTS.cartUpdate, (data) => finish(data, false)),
            subscribe(PUB_SUB_EVENTS.cartError, (data) => finish(data, true)),
          ];
        }

        // Ağ hatasında product-form hiçbir olay yayınlamıyor; buton yüklemede kalmasın
        this.loadingTimeout = setTimeout(() => {
          this.stopWatchingCart();
          this.setLoading(false);
        }, 10000);
      }

      stopWatchingCart() {
        this.cartUnsubscribers?.forEach((unsubscribe) => unsubscribe());
        this.cartUnsubscribers = null;
        clearTimeout(this.loadingTimeout);
      }
    }
  );
}
